import { useForm, Link } from '@inertiajs/react'
import { FormEvent } from 'react'
import PlatformLayout from '@/components/PlatformLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ArrowLeft } from 'lucide-react'

interface School {
  id: number
  name: string
  description: string | null
  phone: string | null
  address: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  country: string | null
  legal_name: string | null
  cnpj: string | null
  privacy_contact_email: string | null
  timezone: string | null
  slug: string | null
}

interface Props {
  school: School
}

export default function Edit({ school }: Props) {
  const initial = {
    name: school.name || '',
    description: school.description || '',
    phone: school.phone || '',
    address: school.address || '',
    city: school.city || '',
    state: school.state || '',
    postal_code: school.postal_code || '',
    country: school.country || 'Brasil',
    legal_name: school.legal_name || '',
    cnpj: school.cnpj || '',
    privacy_contact_email: school.privacy_contact_email || '',
    slug: school.slug || '',
  }

  const { data, setData, patch, processing, errors } = useForm(initial)
  const baseError = (errors as Record<string, string>).base

  const submit = (e: FormEvent) => {
    e.preventDefault()
    patch(`/platform/schools/${school.id}`)
  }

  return (
    <PlatformLayout>
      <div className="p-6 max-w-3xl space-y-6">
        <div>
          <Link href={`/platform/schools/${school.id}`} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> {school.name}
          </Link>
          <h1 className="text-2xl font-bold tracking-tight mt-1">Editar escola</h1>
        </div>

        {baseError && (
          <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm p-3 rounded-md">
            {baseError}
          </div>
        )}

        <form onSubmit={submit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Dados</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="name">Nome *</Label>
                  <Input id="name" value={data.name} onChange={(e) => setData('name', e.target.value)} required />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description">Descrição</Label>
                  <Textarea id="description" rows={3} value={data.description} onChange={(e) => setData('description', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input id="phone" value={data.phone} onChange={(e) => setData('phone', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ</Label>
                  <Input id="cnpj" value={data.cnpj} onChange={(e) => setData('cnpj', e.target.value)} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="legal_name">Razão social</Label>
                  <Input id="legal_name" value={data.legal_name} onChange={(e) => setData('legal_name', e.target.value)} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address">Endereço</Label>
                  <Input id="address" value={data.address} onChange={(e) => setData('address', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">Cidade</Label>
                  <Input id="city" value={data.city} onChange={(e) => setData('city', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">Estado</Label>
                  <Input id="state" value={data.state} onChange={(e) => setData('state', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postal_code">CEP</Label>
                  <Input id="postal_code" value={data.postal_code} onChange={(e) => setData('postal_code', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">País</Label>
                  <Input id="country" value={data.country} onChange={(e) => setData('country', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug (white label)</Label>
                  <Input id="slug" value={data.slug} onChange={(e) => setData('slug', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="privacy_contact_email">Email de privacidade</Label>
                  <Input id="privacy_contact_email" type="email" value={data.privacy_contact_email} onChange={(e) => setData('privacy_contact_email', e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button type="submit" disabled={processing}>
              {processing ? 'Salvando...' : 'Salvar alterações'}
            </Button>
            <Link href={`/platform/schools/${school.id}`}>
              <Button type="button" variant="outline">Cancelar</Button>
            </Link>
          </div>
        </form>
      </div>
    </PlatformLayout>
  )
}
